<script>
import { get } from 'vuex-pathify'

const MAX_RESULTS_COLLAPSED = 3
export default {
	props: {
		receta: Object,
		referenciaRx: Object,
	},
	data() {
		return {
			loading: null,
			productsKey: 1,
			seeMoreResults: false,
			preventOrderUpdate: false,
		}
	},
	inject: ['getPickupPoints', 'getSelectedPickupPoint'],
	computed: {
		selectedPickupPoint() {
			return this.getSelectedPickupPoint()
		},
		order: get('cart/order'),
		orderItems() {
			return (
				this.order?.items.filter((item) => {
					return (
						item.customization?.key == 'recetadigital' && item.customization?.id == this.customizationId
					)
				}) || []
			)
		},
		productsQty() {
			return this.orderItems.reduce((acc, item) => {
				acc[item.refId] = (acc[item.refId] || 0) + item.qty
				return acc
			}, {})
		},

		cartQty() {
			return Object.values(this.productsQty).reduce((acc, qty) => acc + qty, 0)
		},
		maxQtyReached() {
			return this.cartQty >= parseInt(this.referenciaRx.CantidadPrescripta)
		},
		customizationId() {
			return window.btoa([this.receta.id, this.receta.sid, this.referenciaRx.Droga.Codigo].join('|'))
		},
		allProducts() {
			const pickupPoints = this.getPickupPoints()
			const ppToCheck = this.selectedPickupPoint ? [this.selectedPickupPoint] : pickupPoints
			return (this.referenciaRx.products || []).map((p) => {
				const [variant] = p.variants
				const ppWithStock = ppToCheck
					.map((pp) => {
						let qty = variant.pickupStocks?.find((x) => x.pickupPointId == pp.id)?.qty || 0
						return qty > 0 ? { id: pp.id, qty } : null
					})
					.filter(Boolean)

				const ppTotalStock = ppWithStock.reduce((acc, x) => acc + x.qty, 0)
				return { ...p, ppWithStock, ppTotalStock }
			})
		},
		productInCart() {
			return this.allProducts.find((p) => this.productsQty[p.variants[0].id])
		},
		buyableProducts() {
			return this.allProducts
				.filter((x) => x.ppWithStock.length > 0)
				.sort((a, b) => {
					return b.ppWithStock.length - a.ppWithStock.length || b.ppTotalStock - a.ppTotalStock
				})
		},
		showableProducts() {
			if (this.productInCart) {
				return [this.productInCart]
			}
			if (this.seeMoreResults) {
				return this.buyableProducts
			}
			return this.buyableProducts.slice(0, MAX_RESULTS_COLLAPSED)
		},
		moreResultsCount() {
			return Math.max(0, this.buyableProducts.length - MAX_RESULTS_COLLAPSED)
		},
		coberturaItem() {
			return this.receta.coberturaItems.find((x) => x.Droga.Codigo == this.referenciaRx.Droga.Codigo)
		},
		coberturaDiscount() {
			if (!this.coberturaItem?.CantidadAprobada) return 0
			return parseInt(this.coberturaItem.PorcentajeCobertura) || 0
		},
		showApprovedQtyWarning() {
			return (
				this.coberturaItem?.CantidadAprobada > 0 &&
				this.coberturaItem?.CantidadSolicitada > this.coberturaItem?.CantidadAprobada
			)
		},
	},
	watch: {
		order: {
			deep: true,
			handler() {
				if (this.preventOrderUpdate) return
				this.productsKey += 1
			},
		},
	},
	methods: {
		getCodTroquelByEan(ean) {
			return this.referenciaRx.Item.find((x) => x.CodBarras == ean)?.CodTroquel
		},
		async updateProductQty(product, qty) {
			const variantId = product.variants[0].id
			if (qty <= 0) {
				let item = this.orderItems.find((item) => item.refId == variantId)
				if (!item) return
				await this.$shop.deleteCartItem({
					cartItemId: item.cartItemId,
					loading: (b) => (this.loading = b ? variantId : null),
				})
			} else {
				let currentItem = this.orderItems.find((item) => item.customization?.id == this.customizationId)
				if (currentItem && currentItem.refId != variantId) {
					this.preventOrderUpdate = true
					await this.$shop.deleteCartItem({
						cartItemId: currentItem.cartItemId,
						loading: (b) => (this.loading = b ? variantId : null),
					})
					await new Promise((resolve) => {
						this.$nextTick(() => resolve())
					})
					this.preventOrderUpdate = false
				}
				await this.$shop.addToCart({
					productId: product.id,
					variantId: variantId,
					customization: {
						key: 'recetadigital',
						id: this.customizationId,
						data: {
							recetaId: this.receta.id,
							recetaSid: this.receta.sid,
							DrogaCodigo: this.referenciaRx.Droga.Codigo,
							CodTroquel: this.getCodTroquelByEan(product.variants[0].ean),
						},
					},
					qty,
					sum: false,
					showUpsells: false,
					showCart: false,
					loading: (b) => (this.loading = b ? variantId : null),
				})
			}
		},
	},
}
</script>

<template>
	<div class="mt-2 my-sm-2 rounded-lg" style="border: 1px solid #444; overflow: hidden">
		<div
			class="d-flex align-center py-2 px-3"
			style="background: #333; color: #fff; gap: 24px; line-height: 1.1"
		>
			<div>
				<div>
					<b>{{ referenciaRx.GenericoPresentacion }}</b>
				</div>
				<small class="grey--text"
					>{{ referenciaRx.Droga.Descripcion }} / {{ referenciaRx.Droga.Codigo }}</small
				>
			</div>
			<v-spacer />
			<div class="font-2 font-sm-3 nowrap flex-shrink-0">
				{{ cartQty }} / {{ referenciaRx.CantidadPrescripta }}
			</div>
		</div>
		<div class="py-2 px-4" v-if="showableProducts.length" :key="`productsKey-${productsKey}`">
			<div v-if="showApprovedQtyWarning" class="mb-2">
				<v-chip color="orange darken-1 white--text" small style="white-space: normal; height: unset">
					<div>
						La cobertura aplica a un&nbsp;<b
							>máximo de {{ coberturaItem.CantidadAprobada }} unidad{{
								coberturaItem.CantidadAprobada > 1 ? 'es' : ''
							}}</b
						>&nbsp;para esta droga.
					</div>
				</v-chip>
			</div>
			<div v-if="!coberturaDiscount" class="mb-2">
				<v-chip color="orange darken-3 white--text" small style="white-space: normal; height: unset">
					<div class="py-1">
						<div><b>Respecto a la cobertura</b></div>
						Es posible que la cobertura se calcule correctamente cuando hagas la validación final en el
						checkout al finalizar tu compra. Este es un paso previo a realizar el pago, con lo cual
						<b>conocerás el monto final antes de pagar</b>.
					</div>
				</v-chip>
			</div>
			<RecetaDigitalProductItem
				v-for="(product, i) of showableProducts"
				:key="`${i}_${product.variants[0].id}`"
				class="py-3"
				:style="i < showableProducts.length - 1 ? 'border-bottom: 1px solid #bbb' : ''"
				:product="product"
				:qty="parseInt(productsQty[product.variants[0].id] || 0)"
				@update:qty="updateProductQty(product, $event)"
				:loading="loading == product.variants[0].id"
				:other-loading="!!loading && loading != product.variants[0].id"
				:receta="receta"
				:referenciaRx="referenciaRx"
				:num="i + 1"
				:in-cart="!!productInCart"
			/>
			<div class="py-2" v-if="moreResultsCount && !productInCart">
				<Button text small @click="seeMoreResults = !seeMoreResults">
					{{ seeMoreResults ? 'Ver menos opciones' : `Ver más opciones (+${moreResultsCount})` }}
					<v-icon right small>{{ seeMoreResults ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
				</Button>
			</div>
			<div class="py-2" v-if="productInCart">
				<Button
					class="ml-2"
					outlined
					small
					color="orange darken-3"
					@click="updateProductQty(productInCart, 0)"
					:disabled="!!loading"
				>
					<v-icon left small>mdi-swap-horizontal</v-icon> Elegir otra opción
				</Button>
			</div>
		</div>
		<div class="pa-6 text-center" v-else>
			<i v-if="selectedPickupPoint"
				>No tenemos medicamentos disponibles en <b>{{ selectedPickupPoint.name }}</b> para esta droga</i
			>
			<i v-else>No tenemos medicamentos disponibles en ninguna sucursal para esta droga</i>
		</div>
	</div>
</template>

